import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Learn More Page Component
 * Detailed information about The Traders platform and its autonomous capabilities
 */
const LearnMore: React.FC = () => {
  return (
    <div className="min-h-screen bg-neutral-950">
      {/* Navigation Header */}
      <header className="bg-neutral-900 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="text-2xl font-bold text-white hover:text-cyan-400 transition-colors duration-200"
            >
              The <span className="text-cyan-400">Traders</span>
            </Link>
            <Link
              to="/"
              className="text-neutral-300 hover:text-white transition-colors duration-200"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 via-neutral-900 to-neutral-950"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              About <span className="text-cyan-400">The Traders</span>
            </h1>
            <p className="text-xl md:text-2xl text-neutral-300 mb-8 max-w-4xl mx-auto">
              Discover how our autonomous algorithmic trading platform revolutionizes the way you trade without compromising your security or requiring external API keys.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-neutral-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {/* Autonomous Trading Section */}
            <div className="bg-neutral-800 p-8 rounded-lg border border-neutral-700">
              <h2 className="text-3xl font-bold text-white mb-6">
                 Autonomous Trading Technology
              </h2>
              <div className="space-y-4 text-neutral-300">
                <p>
                  The Traders platform operates as a completely autonomous system that doesn't require your personal API keys, 
                  brokerage credentials, or any sensitive financial information. Our revolutionary approach ensures maximum 
                  security while delivering exceptional trading performance.
                </p>
                <p>
                  Unlike traditional trading platforms that require you to connect your own brokerage accounts and expose 
                  your API keys, The Traders operates independently with its own secure infrastructure and proprietary 
                  trading algorithms.
                </p>
              </div>
            </div>

            {/* Security & Privacy Section */}
            <div className="bg-neutral-800 p-8 rounded-lg border border-neutral-700">
              <h2 className="text-3xl font-bold text-white mb-6">
                 Zero Compromise Security
              </h2>
              <div className="space-y-4 text-neutral-300">
                <p>
                  <strong>No API Keys Required:</strong> You never need to provide brokerage API keys, account credentials, 
                  or any sensitive financial information. Your privacy and security are our top priorities.
                </p>
                <p>
                  <strong>Proprietary Infrastructure:</strong> We maintain our own secure trading infrastructure with 
                  enterprise-grade security protocols, ensuring your data remains completely protected.
                </p>
                <p>
                  <strong>Regulated & Compliant:</strong> Our platform operates under strict regulatory compliance, 
                  providing you with peace of mind and legal protection.
                </p>
              </div>
            </div>

            {/* How It Works Section */}
            <div className="bg-neutral-800 p-8 rounded-lg border border-neutral-700">
              <h2 className="text-3xl font-bold text-white mb-6">
                How It Works
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-cyan-400">Traditional Platforms</h3>
                  <ul className="space-y-2 text-neutral-300">
                    <li>• Require your API keys</li>
                    <li>• Access your brokerage account</li>
                    <li>• Store sensitive credentials</li>
                    <li>• Limited by your account permissions</li>
                    <li>• Risk of credential exposure</li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-cyan-400">The Traders Platform</h3>
                  <ul className="space-y-2 text-neutral-300">
                    <li>• No API keys needed</li>
                    <li>• Autonomous operation</li>
                    <li>• Zero credential storage</li>
                    <li>• Unlimited trading capabilities</li>
                    <li>• Maximum security</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* AI & Technology Section */}
            <div className="bg-neutral-800 p-8 rounded-lg border border-neutral-700">
              <h2 className="text-3xl font-bold text-white mb-6">
                 Advanced AI Technology
              </h2>
              <div className="space-y-4 text-neutral-300">
                <p>
                  Our platform leverages cutting-edge artificial intelligence and machine learning algorithms to analyze 
                  market conditions, identify trading opportunities, and execute trades with precision timing.
                </p>
                <p>
                  <strong>Real-time Market Analysis:</strong> Continuous monitoring of global markets across multiple 
                  timeframes and asset classes.
                </p>
                <p>
                  <strong>Predictive Analytics:</strong> Advanced algorithms that identify patterns and predict market 
                  movements before they happen.
                </p>
                <p>
                  <strong>Risk Management:</strong> Sophisticated risk controls that automatically adjust position sizes 
                  and implement stop-losses to protect your capital.
                </p>
              </div>
            </div>

            {/* Benefits Section */}
            <div className="bg-neutral-800 p-8 rounded-lg border border-neutral-700">
              <h2 className="text-3xl font-bold text-white mb-6">
                 Why Choose The Traders
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-white text-sm"></span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Complete Security</h4>
                      <p className="text-neutral-400 text-sm">No sensitive data required or stored</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-white text-sm"></span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">24/7 Operation</h4>
                      <p className="text-neutral-400 text-sm">Continuous trading across all markets</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-white text-sm"></span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">AI-Powered</h4>
                      <p className="text-neutral-400 text-sm">Advanced algorithms for optimal performance</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-white text-sm"></span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Risk Management</h4>
                      <p className="text-neutral-400 text-sm">Automated protection of your capital</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-white text-sm"></span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Transparent</h4>
                      <p className="text-neutral-400 text-sm">Full visibility into all trading activities</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-white text-sm"></span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Regulated</h4>
                      <p className="text-neutral-400 text-sm">Compliant with financial regulations</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-cyan-900/20 to-neutral-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Experience Autonomous Trading?
          </h2>
          <p className="text-xl text-neutral-400 mb-8">
            Join The Traders platform and discover the future of algorithmic trading without compromising your security.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="inline-flex items-center px-8 py-4 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors duration-200"
            >
              Get Started Now
            </Link>
            <Link
              to="/"
              className="inline-flex items-center px-8 py-4 border border-neutral-600 hover:border-neutral-500 text-neutral-300 hover:text-white font-semibold rounded-lg transition-colors duration-200"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LearnMore;
